import { memo, useCallback, useMemo } from 'react';

import { useMutation } from '@zenbusiness/application-commons-graphql';
import { useTheme } from '@zenbusiness/zui/styles';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';

import { TOKEN_TYPES } from '#client/constants/tokens';
import useBankingAccounts from '#client/hooks/useBankingAccounts';
import useCustomerToken from '#client/hooks/useCustomerToken';
import useToastContext from '#client/hooks/useToastContext';
import { UPDATE_COUNTER_PARTY_STATUS_MUTATION } from '#client/mutations';

import AccountCommonV2 from './AccountCommonV2';

function UnarchiveAccount({
  id,
  name,
  bankName,
  last4Digits,
  resetPagination,
  setShowSkeleton,
  handleCloseModal,
  isACHSupported,
  isWireSupported
}) {
  const theme = useTheme();
  const { getCustomerToken } = useCustomerToken({ type: TOKEN_TYPES.COMMON });
  const { createSuccessToast, createErrorToast } = useToastContext();

  const { id: bankingAccountId, loading: isFetchingBankingAccounts } = useBankingAccounts();

  // mutations
  const [updateCounterparty, { loading: loadingUpdatePayeeStatus }] = useMutation(
    UPDATE_COUNTER_PARTY_STATUS_MUTATION,
    {
      noThrow: true
    }
  );

  const handleUpdatePayeeStatus = useCallback(async () => {
    const customerToken = await getCustomerToken();

    if (!customerToken) {
      handleCloseModal();
      return;
    }

    const { error: updatePayeeError } = await updateCounterparty({
      variables: {
        input: {
          counterpartyId: id,
          status: 'active',
          customerToken,
          bankingAccountId,
          idempotencyKey: uuidv4()
        }
      },
      skip: loadingUpdatePayeeStatus
    });

    if (updatePayeeError) {
      // Show toast error
      createErrorToast({
        title: 'Payee was not updated',
        description: 'There was an error updating this payee. Please try again.',
        error: updatePayeeError
      });
    } else {
      // show toast success and close modal
      createSuccessToast({
        title: `${name} has been unarchived`,
        description: `You can still access your transaction history with ${name}`
      });
      setShowSkeleton();
      resetPagination();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    createErrorToast,
    createSuccessToast,
    getCustomerToken,
    loadingUpdatePayeeStatus,
    resetPagination,
    updateCounterparty
  ]);

  const title = `${name} ${last4Digits}`;
  const subtitle = `${bankName}`;

  const statuses = useMemo(() => {
    const list = [];
    if (isACHSupported) {
      list.push({
        status: 'ACH',
        color: 'primary',
        bgColor: theme.palette.background.grey.dark
      });
    }

    if (isWireSupported) {
      list.push({
        status: 'Wire',
        color: 'primary',
        bgColor: theme.palette.background.grey.dark
      });
    }

    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isACHSupported, isWireSupported]);

  return (
    <AccountCommonV2
      buttonProps={{
        variant: 'outlined'
      }}
      buttonText="Unarchive"
      disabled={loadingUpdatePayeeStatus}
      loading={loadingUpdatePayeeStatus || isFetchingBankingAccounts}
      numberOfCharacters={1}
      onClick={handleUpdatePayeeStatus}
      pillText="Archived"
      statuses={statuses}
      subtitle={subtitle}
      title={title}
    />
  );
}

UnarchiveAccount.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  bankName: PropTypes.string.isRequired,
  last4Digits: PropTypes.string.isRequired,
  resetPagination: PropTypes.func,
  setShowSkeleton: PropTypes.func,
  handleCloseModal: PropTypes.func,
  isACHSupported: PropTypes.bool,
  isWireSupported: PropTypes.bool
};

export default memo(UnarchiveAccount);
